import { configClient, exportClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { triggerUrlDownload } from '~/utils/download-file';

import { CONFIG_TABLE_MAX_PAGE_SIZE } from '../constants/config-table';
import { toConfigTableColumns, toConfigTablePage } from '../mappers/config-table.mapper';
import type {
  ExportResponseApi,
  SortOrderApi,
  TableColumnsRequestApi,
  TableColumnsResponseApi,
  TableExportFormatApi,
  TableExportRequestApi,
  TableExportResultApi,
  TableRowsRequestApi,
  TableRowsResponseApi,
} from '../types/config-table.api.types';
import type {
  ConfigTableColumnMeta,
  ConfigTablePage,
  ConfigTableQuery,
} from '../types/config-table.types';

/**
 * The three calls behind every config-table listing. All parameterised by
 * `tableName`, because both backends are: agentic-config resolves it against
 * `REGISTRY`, the export service against `OVERLAYS`, and an unregistered name is
 * a 400 on either rather than a passthrough.
 *
 * **Two services, one payload.** The grid comes from agentic-config; the export
 * is a different service that reads the same database directly and so has no
 * 100-row page cap to page around. They take the same `filters`/`search` object,
 * which is what lets one per-table `toQuery` feed both — and why an exported file
 * always holds exactly the rows on screen.
 */

export interface FetchConfigTableRowsParams<TRowApi, TRow> {
  tableName: string;
  query: ConfigTableQuery;
  /** 1-based, as the backend's `PageParams` expects. */
  page: number;
  size: number;
  /** Must be a column the server reports as sortable — a non-sortable one is a
   *  `400`. Column definitions gate this by disabling the header control on the
   *  columns their registry excludes. */
  sortBy: string;
  sortOrder: SortOrderApi;
  mapRow: (row: TRowApi) => TRow;
}

/**
 * Fetches one page of rows via the generic `POST /tables/rows`.
 *
 * Server-side filtering, sorting and pagination are mandatory rather than an
 * optimisation: `taxonomy_entries` holds 127,504 rows and the endpoint caps a
 * page at 100, so there is no client-side mode to fall back to.
 *
 * `columns` is deliberately omitted rather than enumerated — that is what makes
 * every column present on each row, so the response rows really are the caller's
 * `TRowApi` and `mapRow` needs no cast.
 */
export async function fetchConfigTableRows<TRowApi, TRow>(
  params: FetchConfigTableRowsParams<TRowApi, TRow>,
): Promise<ConfigTablePage<TRow>> {
  const body: TableRowsRequestApi = {
    table_name: params.tableName,
    ...params.query,
    pagination: {
      page: params.page,
      size: Math.min(params.size, CONFIG_TABLE_MAX_PAGE_SIZE),
      sort_by: params.sortBy,
      sort_order: params.sortOrder,
    },
  };

  const response = await configClient
    .post(endpoints.configuration.tableRows(), { json: body })
    .json<TableRowsResponseApi<TRowApi>>();

  return toConfigTablePage(response, params.mapRow);
}

/**
 * Fetches the column metadata and filter dropdown values for one table via
 * `POST /tables/columns`.
 *
 * One call per table per session, never per keystroke: the endpoint runs a
 * `SELECT DISTINCT` per faceted column (~74–103 ms with values, ~1 ms without).
 *
 * Returns the whole column description rather than only the facet values. The
 * filter bars want just the values and project to them via `toConfigTableFacets`;
 * the AI-filter chips also need each column's `label` to name a column the model
 * chose. Both read this one cached response, so the wider shape costs no request.
 */
export async function fetchConfigTableColumns(tableName: string): Promise<ConfigTableColumnMeta[]> {
  const body: TableColumnsRequestApi = {
    table_name: tableName,
    include_values: true,
  };

  const response = await configClient
    .post(endpoints.configuration.tableColumns(), { json: body })
    .json<TableColumnsResponseApi>();

  return toConfigTableColumns(response);
}

export interface ExportConfigTableParams {
  tableName: string;
  query: ConfigTableQuery;
  /** Must be a sortable column, for the same reason as `fetchConfigTableRows`. */
  sortBy: string;
  sortOrder: SortOrderApi;
  format: TableExportFormatApi;
}

/**
 * Exports every row matching the current filters — not just the visible page —
 * and starts the browser download.
 *
 * `exportClient` rather than `configClient`: this is `POST /config/tables/export`
 * on agentic-export-service, which reads the same agentic-config database
 * read-only. `columns` is omitted so the file carries every column, matching the
 * table's own projection.
 */
export async function exportConfigTable(
  params: ExportConfigTableParams,
): Promise<TableExportResultApi> {
  const body: TableExportRequestApi = {
    tableName: params.tableName,
    ...params.query,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    format: params.format,
  };

  const response = await exportClient
    .post(endpoints.configTables.export(), { json: body })
    .json<ExportResponseApi<TableExportResultApi>>();

  triggerUrlDownload(response.data.url);
  return response.data;
}
